import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import ReactPortal from 'components/global/ReactPortal';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import useAccessControl from 'hooks/useAccessControl';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';

import {
	routeMapping,
	routeMappingForBranch,
	routeMappingForWarehouse
} from 'pages/Shipments/utils/shipmentStatusMapping';

import { ORDERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import * as S from './SearchOrder.styled';

/**
 * @author @buunsal
 *
 */

const SearchOrder = () => {
	const navigate = useNavigate();
	const axios = useAxiosPrivate();
	const emptyArraySkeleton = useMemo(() => Array(5).fill(0), []);
	const userType = useSelector((state) => state.user.userType);
	const assignedWarehouses = useSelector((state) => state.user.assignedWarehouses);
	const assignedBranches = useSelector((state) => state.user.assignedBranches);

	const isAuthorized = useAccessControl();
	const isOperationAccount = isAuthorized('/topbar/button/searchOrderForOperation');

	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([]);
	const [open, setOpen] = useState(false);
	const [searchKey, setSearchKey] = useState(null);
	const debouncedSearchKey = useDebounce(searchKey, 500);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const middleware = useMemo(
		() => [
			flip(),
			shift({
				mainAxis: false
			}),
			offset({
				mainAxis: 8,
				crossAxis: 0
			}),
			size({
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = availableHeight
						? `min(33rem, calc(${availableHeight}px - 5.5rem))`
						: '33rem';
				}
			})
		],
		[]
	);

	const { refs, context, floatingStyles } = useFloating({
		placement: 'bottom-start',
		open,
		onOpenChange: setOpen,
		middleware,
		whileElementsMounted: autoUpdate
	});

	const dismiss = useDismiss(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

	const fetchData = useCallback(
		async (abortController) => {
			try {
				const url = isOperationAccount
					? ORDERS.GET_ORDERS_BY_SEARCH_FOR_OPERATION
					: ORDERS.GET_ORDERS_BY_SEARCH;

				const body = isOperationAccount
					? {
							search: debouncedSearchKey,
							totalRecord: false
						}
					: {
							filter: [
								...(debouncedSearchKey
									? [
											{
												key: ['orderID', 'otoID'],
												action: 'startsWith',
												value: [debouncedSearchKey]
											}
										]
									: []),
								...(assignedWarehouses?.length > 0
									? [
											{
												key: ['warehouseID'],
												action: 'contains',
												value: assignedWarehouses?.map((warehouse) => warehouse.ID)
											}
										]
									: []),
								...(assignedBranches?.length > 0
									? [
											{
												key: ['branchID'],
												action: 'contains',
												value: assignedBranches?.map((branch) => branch.ID)
											}
										]
									: [])
							]
						};

				const response = await axios.post(url, body, {
					signal: abortController?.signal
				});

				if (response.status === 200) {
					setData(response.data.data);
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				if (!abortController?.signal.aborted) {
					AppLogger.error(error);
				}
			}

			setLoading(false);
		},
		[axios, assignedBranches, assignedWarehouses, debouncedSearchKey]
	);

	const reverseString = (str) => {
		const strArr = str.split('');
		const reversedArr = strArr.reverse();
		return reversedArr.join('');
	};
	const markedString = ({ text }) => {
		let starterIndex = 0;
		let lastIndex = 0;

		const textLowerCase = text.toLowerCase();

		if (searchKey && textLowerCase.includes(searchKey)) {
			// reverse  text string
			const reversedText = reverseString(textLowerCase);

			// reverse id string
			const reversedIdText = reverseString(searchKey);

			for (let i = 0; i < reversedText?.length; i += 1) {
				const splicedText = reversedText?.slice(i);

				if (splicedText?.includes(reversedIdText)) {
					lastIndex = i;
				}
			}
			lastIndex = text.length - lastIndex - 1;
			starterIndex = lastIndex - searchKey.length + 1;

			const leftString = text.slice(0, starterIndex);
			const middleString = text.slice(starterIndex, lastIndex + 1);
			const rightString = text.slice(lastIndex + 1, text.length);

			return (
				<Typography key={searchKey} noWrap>
					<span>{leftString}</span>
					<span
						style={{
							backgroundColor: 'var(--search-order-bg)',
							borderRadius: '.5rem'
						}}>
						{middleString}
					</span>
					<span>{rightString}</span>
				</Typography>
			);
		}
		return (
			<Typography key={searchKey} noWrap>
				{text}
			</Typography>
		);
	};

	useEffect(() => {
		if (searchKey?.length > 2) {
			setOpen(true);

			if (searchKey === debouncedSearchKey) {
				setLoading(false);
			} else {
				setLoading(true);
			}
		} else {
			setOpen(false);
		}
	}, [searchKey]);

	useEffect(() => {
		const abortController = new AbortController();

		if (debouncedSearchKey?.length > 2) {
			fetchData(abortController);
		}

		return () => {
			abortController && abortController.abort();
		};
	}, [debouncedSearchKey]);

	const onClickRow = (order) => {
		setOpen(false);
		const state = {
			defaultFilters: [
				{
					column: {
						Header: 'orderID',
						accessor: 'orderID',
						filterType: FilterType.string
					},
					action: FilterAction.is,
					value: order.orderID
				}
			]
		};

		if (isOperationAccount) {
			navigate(mergePaths(PATHS.OPERATION_SHIPMENT, PATHS.OPERATION_ALL_ORDERS), {
				state
			});
			return;
		}

		const routeMap =
			userType === 'branchUser'
				? routeMappingForBranch
				: userType === 'warehouseUser'
					? routeMappingForWarehouse
					: routeMapping;

		if (!routeMap[order?.status]) {
			AppLogger.error('routerMap[order?.status] is undefined; Could not find the status => ', order?.status);
			return;
		}

		navigate(routeMap[order?.status], {
			state
		});
	};

	const handleOnFocus = () => {
		if (searchKey?.length > 2) {
			setOpen(true);
		}
	};

	return (
		<S.SearchInputContainer ref={refs.setReference} {...getReferenceProps()}>
			<SearchInput
				placeholder="searchOrder"
				value={searchKey}
				setValue={setSearchKey}
				onFocus={handleOnFocus}
				expandable={!isMobile}
			/>
			{open && (
				<ReactPortal wrapperId="searchorder-portal">
					<S.PopperContainer ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
						<S.Table>
							{loading ? (
								emptyArraySkeleton.map((_, index) => (
									<S.Row key={index}>
										<div className="col-3">
											<Skeleton height="2rem" width="100%" />
										</div>
										<div className="col-3 mx-1">
											<Skeleton height="2rem" width="100%" />
										</div>
										<div className="col-3 mx-1">
											<Skeleton height="2rem" width="100%" />
										</div>
										<div className="col-3">
											<Skeleton height="2rem" width="100%" />
										</div>
									</S.Row>
								))
							) : data.length > 0 ? (
								data.map((order, index) => (
									<S.Row key={index} onClick={() => onClickRow(order)}>
										<div className="col-3">
											{markedString({
												text: order.orderID?.toString()
											})}
										</div>
										<div className="col-3">
											<Typography>{order.shippingCompany ?? '-'}</Typography>
										</div>
										<div className="col-3">
											{markedString({
												text: order.otoID?.toString()
											})}
										</div>
										<div className="col-3">
											<Status value={order.status ? order.status : null} />
										</div>
									</S.Row>
								))
							) : (
								<S.Row>
									<Typography>searchOrder.NoData</Typography>
								</S.Row>
							)}
						</S.Table>
					</S.PopperContainer>
				</ReactPortal>
			)}
		</S.SearchInputContainer>
	);
};

export default SearchOrder;
